import {
  ChatSDKError,
  stripRuntimeContextText,
  type ErrorCode,
} from "@tracify/shared";

const DEFAULT_CHAT_ERROR_CODE: ErrorCode = "bad_request:api";

function formatResponseStatus(response: Response) {
  return `${response.status}${
    response.statusText ? ` ${response.statusText}` : ""
  }`;
}

async function toChatSdkError(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.toLowerCase().includes("application/json")) {
    try {
      const body = (await response.json()) as {
        code?: unknown;
        cause?: unknown;
      };

      if (typeof body.code === "string") {
        return new ChatSDKError(
          body.code as ErrorCode,
          typeof body.cause === "string" ? body.cause : undefined
        );
      }
    } catch {
      // Fall through to a generic, typed chat error.
    }
  }

  return new ChatSDKError(
    DEFAULT_CHAT_ERROR_CODE,
    `Chat request failed with ${formatResponseStatus(response)}.`
  );
}

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      throw await toChatSdkError(response);
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new ChatSDKError("offline:chat");
    }

    throw error;
  }
}

export function sanitizeText(text: string) {
  const contextStrippedText = stripRuntimeContextText(text);
  const { cleanedText } = extractInlineReasoning(contextStrippedText);
  return cleanedText.replace("<has_function_call>", "");
}

const INLINE_REASONING_TAGS = ["think", "thinking", "analysis", "reasoning"];
const INLINE_REASONING_REGEX = new RegExp(
  `<(${INLINE_REASONING_TAGS.join("|")})>([\\s\\S]*?)<\\/\\1>`,
  "gi"
);

function extractInlineReasoning(text: string): {
  cleanedText: string;
  reasoning: string[];
} {
  const reasoning: string[] = [];

  const cleanedText = text.replace(
    INLINE_REASONING_REGEX,
    (_match, _tag: string, content: string) => {
      const trimmed = content.trim();
      if (trimmed.length > 0) {
        reasoning.push(trimmed);
      }
      return "\n";
    }
  );

  return {
    cleanedText: cleanedText.replace(/\n{3,}/g, "\n\n"),
    reasoning,
  };
}
